/**
 * EmailTemplatesUnifiedCard — Admin Diagnostics (2026-02-17)
 *
 * READ-ONLY. Hits GET /api/admin/diagnostics/email-templates-unified
 * and renders the tenant's unified email-template list — name,
 * category, source store — so the consolidated set can be verified.
 * No edit, delete, or send controls.
 */
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Mail, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

function authHeaders() {
  const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function Stat({ label, value, tone = 'slate' }) {
  const map = {
    slate: 'bg-slate-100 text-slate-900 border-slate-300',
    emerald: 'bg-emerald-100 text-emerald-900 border-emerald-300',
    indigo: 'bg-indigo-100 text-indigo-900 border-indigo-300',
  };
  return (
    <div className={`p-3 rounded-lg border ${map[tone]}`}>
      <div className="text-[10px] uppercase tracking-wide opacity-70 font-semibold">{label}</div>
      <div className="text-2xl font-mono font-semibold">{value}</div>
    </div>
  );
}

export default function EmailTemplatesUnifiedCard() {
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API}/api/admin/diagnostics/email-templates-unified`,
        { headers: authHeaders() },
      );
      setData(res.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to load';
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const templates = data?.templates || [];
  const counts = data?.counts || {};

  return (
    <div
      className="rounded-lg border bg-white p-4 space-y-3"
      data-testid="email-templates-unified-card"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Mail className="w-5 h-5 text-slate-700" />
          <h3 className="font-semibold text-slate-900">Unified Email Templates</h3>
          <Badge className="bg-slate-100 text-slate-800 border-slate-300">Read-only</Badge>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={load}
          disabled={busy}
          data-testid="email-templates-unified-refresh-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
          {data ? 'Refresh' : 'Load'}
        </Button>
      </div>
      <p className="text-xs text-slate-600">
        Combined view of <code>email_templates</code> + <code>journey_email_templates</code> for the current tenant.
        Deduped by name (tenant-customized wins over system). Test artifacts (A-only-template-*) are excluded.
      </p>

      {error && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
          {error}
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-3 gap-2">
            <Stat label="Total" value={counts.total ?? templates.length} tone="slate" />
            <Stat label="journey_email_templates" value={counts.journey_email_templates ?? 0} tone="indigo" />
            <Stat label="email_templates" value={counts.email_templates ?? 0} tone="emerald" />
          </div>

          <div className="border rounded overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-100 text-left">
                <tr>
                  <th className="p-2">Name</th>
                  <th className="p-2">Category</th>
                  <th className="p-2">Source</th>
                  <th className="p-2">System?</th>
                </tr>
              </thead>
              <tbody data-testid="email-templates-unified-tbody">
                {templates.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="p-3 italic text-slate-500 text-center">
                      No templates in this tenant.
                    </td>
                  </tr>
                ) : templates.map((t, i) => (
                  <tr
                    key={`${t.source}-${t.id || i}`}
                    className="border-t hover:bg-slate-50"
                    data-testid={`email-templates-unified-row-${i}`}
                  >
                    <td className="p-2 font-medium text-slate-900">{t.name || <em>(unnamed)</em>}</td>
                    <td className="p-2">
                      <code className="text-[11px] text-slate-700">{t.category || '—'}</code>
                    </td>
                    <td className="p-2">
                      <Badge
                        className={
                          t.source === 'journey_email_templates'
                            ? 'bg-indigo-50 text-indigo-800 border-indigo-200 text-[10px]'
                            : 'bg-emerald-50 text-emerald-800 border-emerald-200 text-[10px]'
                        }
                      >
                        {t.source}
                      </Badge>
                    </td>
                    <td className="p-2">
                      {t.is_system ? (
                        <span className="text-[11px] text-slate-600">system</span>
                      ) : (
                        <span className="text-[11px] text-slate-800 font-medium">tenant</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
